// Pro / Season Pass entitlement state (email + flags) with backend sync.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_KEY = "parking_pro_email";
const IS_PRO_KEY = "parking_pro_is_pro";
const IS_SEASON_KEY = "parking_pro_is_season";
const SEASON_EXPIRES_KEY = "parking_pro_season_expires";
const PLAN_KEY = "parking_pro_plan";
const TRIAL_KEY = "parking_pro_trial_session"; // ISO date string when trial was used

export function getEmail() {
  return (localStorage.getItem(EMAIL_KEY) || "").trim().toLowerCase();
}
export function setEmail(email) {
  localStorage.setItem(EMAIL_KEY, (email || "").trim().toLowerCase());
}
export function clearPro() {
  localStorage.removeItem(EMAIL_KEY);
  localStorage.removeItem(IS_PRO_KEY);
  localStorage.removeItem(IS_SEASON_KEY);
  localStorage.removeItem(SEASON_EXPIRES_KEY);
  localStorage.removeItem(PLAN_KEY);
}

export function getIsPro() {
  return localStorage.getItem(IS_PRO_KEY) === "true";
}
export function setIsPro(flag) {
  localStorage.setItem(IS_PRO_KEY, flag ? "true" : "false");
}

// Season Pass state ---------------------------------------------------------
export function getIsSeason() {
  if (localStorage.getItem(IS_SEASON_KEY) !== "true") return false;
  // Client-side expiry guard: don't consider season active if past expiry.
  const iso = localStorage.getItem(SEASON_EXPIRES_KEY);
  if (!iso) return true; // subscription-driven (season_monthly)
  try {
    return new Date(iso).getTime() > Date.now();
  } catch {
    return true;
  }
}
export function setIsSeason(flag) {
  localStorage.setItem(IS_SEASON_KEY, flag ? "true" : "false");
}
export function getSeasonExpiresAt() {
  const iso = localStorage.getItem(SEASON_EXPIRES_KEY);
  return iso || null;
}
export function setSeasonExpiresAt(iso) {
  if (iso) localStorage.setItem(SEASON_EXPIRES_KEY, iso);
  else localStorage.removeItem(SEASON_EXPIRES_KEY);
}
export function getPlan() {
  return localStorage.getItem(PLAN_KEY) || null;
}
export function setPlan(plan) {
  if (plan) localStorage.setItem(PLAN_KEY, plan);
  else localStorage.removeItem(PLAN_KEY);
}

export async function refreshProStatus(email) {
  const target = (email || getEmail()).trim().toLowerCase();
  if (!target) {
    setIsPro(false);
    setIsSeason(false);
    setSeasonExpiresAt(null);
    setPlan(null);
    return { is_pro: false, is_season: false, email: "" };
  }
  try {
    const res = await axios.post(`${API}/pro/status`, { email: target });
    const d = res.data || {};
    setEmail(target);
    setIsPro(!!d.is_pro);
    setIsSeason(!!d.is_season);
    setSeasonExpiresAt(d.season_expires_at || null);
    setPlan(d.plan || null);
    return d;
  } catch (e) {
    // Keep last known value if server unreachable
    return { is_pro: getIsPro(), is_season: getIsSeason(), email: target };
  }
}

// Trial: allow first 60s of Pro-locked mode per browser session
export function trialAvailable() {
  return !sessionStorage.getItem(TRIAL_KEY);
}
export function startTrial() {
  sessionStorage.setItem(TRIAL_KEY, new Date().toISOString());
}
export function trialElapsedMs() {
  const iso = sessionStorage.getItem(TRIAL_KEY);
  if (!iso) return 0;
  return Date.now() - new Date(iso).getTime();
}
export const TRIAL_MS = 60_000;

export async function startCheckout({ lookup_key, email, coupon_code }) {
  const origin_url = window.location.origin;
  const body = { lookup_key, email, origin_url };
  if (coupon_code) body.coupon_code = coupon_code;
  const res = await axios.post(`${API}/payments/checkout`, body);
  return res.data; // { checkout_url, session_id }
}

export async function validateCoupon(code) {
  const res = await axios.post(`${API}/coupons/validate`, { code });
  return res.data; // { id, percent_off, amount_off, currency, duration }
}

export async function pollPaymentStatus(session_id, maxAttempts = 30) {
  for (let i = 0; i < maxAttempts; i++) {
    const res = await axios.get(`${API}/payments/status/${session_id}`);
    if (res.data.payment_status === "paid") return res.data;
    if (["failed", "expired"].includes(res.data.payment_status)) return res.data;
    await new Promise((r) => setTimeout(r, 2000));
  }
  return { payment_status: "timeout", session_id };
}
